// usePip — 把 <video> 送進瀏覽器原生「畫中畫」懸浮窗（APP#18 web A）。
//
// 標準路徑：HTMLVideoElement.requestPictureInPicture()（Chrome/Edge/Firefox/
// Safari 16.4+）。離開頁面/切到別的 app 時懸浮窗持續播放（OS 行為）。
// Safari 舊版（無 requestPictureInPicture 但有 webkitSetPresentationMode）走
// fallback：video.webkitSetPresentationMode('picture-in-picture')。
//
// 狀態以原生事件 enterpictureinpicture/leavepictureinpicture 為準（使用者也可
// 從懸浮窗自身關閉，必須跟著更新），不只信任 toggle 的回傳。
//
// 純特性偵測（pipSupported / 元素是否能進 PiP）抽到 pipSupport.ts 供單元測試；
// 本 hook 只編排副作用，失敗一律吞掉不丟例外（降級為「按了沒反應」而非崩潰）。

import { useCallback, useEffect, useState } from 'react';
import { elementCanPip, pipSupported, type PipVideoElement } from './pipSupport';

export interface PipController {
  /** 此瀏覽器是否支援 video PiP（標準或 webkit fallback）。 */
  supported: boolean;
  /** 目前是否在懸浮窗中。 */
  active: boolean;
  /** 來源是否就緒（有畫面才能進 PiP）；false 時按鈕應 disabled。 */
  ready: boolean;
  /** 進/出懸浮窗（依目前狀態切換）。回傳是否成功觸發。 */
  toggle(): Promise<boolean>;
}

/**
 * @param videoRef 要送進懸浮窗的 <video>
 * @param ready    來源是否就緒（live=streaming 且有串流；回放=有 src）
 */
export function usePip(videoRef: React.RefObject<HTMLVideoElement | null>, ready: boolean): PipController {
  const supported = pipSupported();
  const [active, setActive] = useState(false);

  // 以原生事件為單一真相（含使用者從懸浮窗自行關閉、或被瀏覽器自動關閉）。
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onEnter = () => setActive(true);
    const onLeave = () => setActive(false);
    v.addEventListener('enterpictureinpicture', onEnter);
    v.addEventListener('leavepictureinpicture', onLeave);
    // webkit fallback：presentationmode 變更事件
    const vk = v as PipVideoElement;
    const onWebkit = () => setActive(vk.webkitPresentationMode === 'picture-in-picture');
    if (typeof vk.webkitSetPresentationMode === 'function') {
      v.addEventListener('webkitpresentationmodechanged', onWebkit);
    }
    return () => {
      v.removeEventListener('enterpictureinpicture', onEnter);
      v.removeEventListener('leavepictureinpicture', onLeave);
      v.removeEventListener('webkitpresentationmodechanged', onWebkit);
    };
  }, [videoRef]);

  // 來源消失（斷線 / 離開頁面）→ 退出懸浮窗，避免懸空播放黑畫面。
  useEffect(() => {
    if (ready) return;
    const v = videoRef.current as PipVideoElement | null;
    if (!v) return;
    const doc = document as Document & { exitPictureInPicture?: () => Promise<void>; pictureInPictureElement?: Element | null };
    if (doc.pictureInPictureElement === v) void doc.exitPictureInPicture?.().catch(() => {});
    else if (typeof v.webkitSetPresentationMode === 'function' && v.webkitPresentationMode === 'picture-in-picture') {
      try {
        v.webkitSetPresentationMode('inline');
      } catch {
        /* already inline */
      }
    }
  }, [ready, videoRef]);

  const toggle = useCallback(async (): Promise<boolean> => {
    const v = videoRef.current as PipVideoElement | null;
    if (!v || !elementCanPip(v)) return false;
    const doc = document as Document & { exitPictureInPicture?: () => Promise<void>; pictureInPictureElement?: Element | null };
    try {
      // 標準路徑
      if (typeof v.requestPictureInPicture === 'function') {
        if (doc.pictureInPictureElement === v) {
          await doc.exitPictureInPicture?.();
        } else {
          await v.requestPictureInPicture();
        }
        return true;
      }
      // webkit fallback（舊 Safari）
      if (typeof v.webkitSetPresentationMode === 'function') {
        const next = v.webkitPresentationMode === 'picture-in-picture' ? 'inline' : 'picture-in-picture';
        v.webkitSetPresentationMode(next);
        return true;
      }
      return false;
    } catch {
      // 使用者手勢缺失 / 元素無畫面 / 已在切換中 → 不崩潰，按了沒反應
      return false;
    }
  }, [videoRef]);

  return { supported, active, ready, toggle };
}
